import type { Dataset, DatasetSample } from '../types.ts';

const DATASETS: Record<string, {
  name: string;
  classNames: string[];
  trainImages: string;
  trainLabels: string;
  testImages: string;
  testLabels: string;
}> = {
  mnist: {
    name: 'MNIST',
    classNames: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
    trainImages: './data/train-images-idx3-ubyte.gz',
    trainLabels: './data/train-labels-idx1-ubyte.gz',
    testImages: './data/t10k-images-idx3-ubyte.gz',
    testLabels: './data/t10k-labels-idx1-ubyte.gz',
  },
  fashion: {
    name: 'Fashion-MNIST',
    classNames: ['T-shirt', 'Trouser', 'Pullover', 'Dress', 'Coat', 'Sandal', 'Shirt', 'Sneaker', 'Bag', 'Ankle boot'],
    trainImages: './data/fashion-train-images-idx3-ubyte.gz',
    trainLabels: './data/fashion-train-labels-idx1-ubyte.gz',
    testImages: './data/fashion-t10k-images-idx3-ubyte.gz',
    testLabels: './data/fashion-t10k-labels-idx1-ubyte.gz',
  },
  kmnist: {
    name: 'KMNIST',
    classNames: ['お', 'き', 'す', 'つ', 'な', 'は', 'ま', 'や', 'れ', 'を'],
    trainImages: './data/kmnist-train-images-idx3-ubyte.gz',
    trainLabels: './data/kmnist-train-labels-idx1-ubyte.gz',
    testImages: './data/kmnist-test-images-idx3-ubyte.gz',
    testLabels: './data/kmnist-test-labels-idx1-ubyte.gz',
  },
  'quickdraw-face': {
    name: 'QuickDraw: Faces',
    classNames: ['face'],
    trainImages: './data/quickdraw-face-train-images-idx3-ubyte.gz',
    trainLabels: './data/quickdraw-face-train-labels-idx1-ubyte.gz',
    testImages: './data/quickdraw-face-test-images-idx3-ubyte.gz',
    testLabels: './data/quickdraw-face-test-labels-idx1-ubyte.gz',
  },
  'quickdraw-flower': {
    name: 'QuickDraw: Flowers',
    classNames: ['flower'],
    trainImages: './data/quickdraw-flower-train-images-idx3-ubyte.gz',
    trainLabels: './data/quickdraw-flower-train-labels-idx1-ubyte.gz',
    testImages: './data/quickdraw-flower-test-images-idx3-ubyte.gz',
    testLabels: './data/quickdraw-flower-test-labels-idx1-ubyte.gz',
  },
  'quickdraw-tree': {
    name: 'QuickDraw: Trees',
    classNames: ['tree'],
    trainImages: './data/quickdraw-tree-train-images-idx3-ubyte.gz',
    trainLabels: './data/quickdraw-tree-train-labels-idx1-ubyte.gz',
    testImages: './data/quickdraw-tree-test-images-idx3-ubyte.gz',
    testLabels: './data/quickdraw-tree-test-labels-idx1-ubyte.gz',
  },
  'quickdraw-cat': {
    name: 'QuickDraw: Cats',
    classNames: ['cat'],
    trainImages: './data/quickdraw-cat-train-images-idx3-ubyte.gz',
    trainLabels: './data/quickdraw-cat-train-labels-idx1-ubyte.gz',
    testImages: './data/quickdraw-cat-test-images-idx3-ubyte.gz',
    testLabels: './data/quickdraw-cat-test-labels-idx1-ubyte.gz',
  },
  'quickdraw-butterfly': {
    name: 'QuickDraw: Butterflies',
    classNames: ['butterfly'],
    trainImages: './data/quickdraw-butterfly-train-images-idx3-ubyte.gz',
    trainLabels: './data/quickdraw-butterfly-train-labels-idx1-ubyte.gz',
    testImages: './data/quickdraw-butterfly-test-images-idx3-ubyte.gz',
    testLabels: './data/quickdraw-butterfly-test-labels-idx1-ubyte.gz',
  },
};

const cache: Map<string, Dataset> = new Map();

async function decompressGzip(buffer: ArrayBuffer): Promise<Uint8Array> {
  if (typeof DecompressionStream !== 'undefined') {
    const ds = new DecompressionStream('gzip');
    const writer = ds.writable.getWriter();
    writer.write(new Uint8Array(buffer));
    writer.close();
    const chunks: Uint8Array[] = [];
    const reader = ds.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }
  throw new Error('Gzip decompression not supported in this browser');
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }
  const buffer = await response.arrayBuffer();
  if (url.endsWith('.gz')) {
    return decompressGzip(buffer);
  }
  return new Uint8Array(buffer);
}

function parseImages(bytes: Uint8Array, maxCount?: number): { images: Float32Array[]; width: number; height: number } {
  const dataView = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const magic = dataView.getInt32(0, false);
  if (magic !== 2051) {
    throw new Error(`Invalid image magic number: ${magic}`);
  }
  const count = dataView.getInt32(4, false);
  const rows = dataView.getInt32(8, false);
  const cols = dataView.getInt32(12, false);
  const actualCount = maxCount ? Math.min(count, maxCount) : count;
  const pixelCount = rows * cols;
  const images: Float32Array[] = [];
  const headerOffset = 16;

  for (let i = 0; i < actualCount; i++) {
    const img = new Float32Array(pixelCount);
    for (let j = 0; j < pixelCount; j++) {
      img[j] = bytes[headerOffset + i * pixelCount + j] / 255;
    }
    images.push(img);
  }

  return { images, width: cols, height: rows };
}

function parseLabels(bytes: Uint8Array, maxCount?: number): number[] {
  const dataView = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const magic = dataView.getInt32(0, false);
  if (magic !== 2049) {
    throw new Error(`Invalid label magic number: ${magic}`);
  }
  const count = dataView.getInt32(4, false);
  const actualCount = maxCount ? Math.min(count, maxCount) : count;
  const labels: number[] = [];
  const headerOffset = 8;

  for (let i = 0; i < actualCount; i++) {
    labels.push(bytes[headerOffset + i]);
  }

  return labels;
}

function combine(images: Float32Array[], labels: number[], width: number, height: number): DatasetSample[] {
  return images.map((image, i) => ({
    image,
    label: labels[i],
    width,
    height,
  }));
}

export async function loadDataset(type: string): Promise<Dataset> {
  if (type === 'mnist-small' && cache.has('mnist')) {
    const ds = cache.get('mnist')!;
    return {
      ...ds,
      name: 'MNIST Reduced',
      train: ds.train.slice(0, 10000),
    };
  }

  if (cache.has(type)) {
    return cache.get(type)!;
  }

  const meta = DATASETS[type];
  if (!meta) {
    throw new Error(`Unknown dataset: ${type}`);
  }

  const maxTrain = type === 'mnist-small' ? 10000 : undefined;

  const [trainImgBytes, trainLabelBytes, testImgBytes, testLabelBytes] = await Promise.all([
    fetchBytes(meta.trainImages),
    fetchBytes(meta.trainLabels),
    fetchBytes(meta.testImages),
    fetchBytes(meta.testLabels),
  ]);

  const trainImages = parseImages(trainImgBytes, maxTrain);
  const trainLabels = parseLabels(trainLabelBytes, maxTrain);
  const testImages = parseImages(testImgBytes);
  const testLabels = parseLabels(testLabelBytes);

  const dataset: Dataset = {
    name: type === 'mnist-small' ? 'MNIST Reduced' : meta.name,
    train: combine(trainImages.images, trainLabels, trainImages.width, trainImages.height),
    test: combine(testImages.images, testLabels, testImages.width, testImages.height),
    numClasses: meta.classNames.length,
    classNames: meta.classNames,
  };

  if (type !== 'mnist-small') {
    cache.set(type, dataset);
  }

  return dataset;
}
